import { api } from "../api";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { closeApp, haptic } from "../telegram";
import { useGame } from "../useGame";

interface View {
  q: string;
  options: string[];
  pick?: number;
  answered: boolean;
}

const COPY = { bad_pick: "Обери один з варіантів" };

export function Stado({ date }: { date?: string | undefined }) {
  const g = useGame<View>("stado", COPY, date);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const pick = v.pick ?? null;

  // Everyone's choice comes back as score = option index.
  const votes = s.group.filter((x) => x.status === "won" && x.score !== null);
  const counts = v.options.map((_, i) => votes.filter((x) => x.score === i).length);
  const total = counts.reduce((a, b) => a + b, 0);
  const top = Math.max(...counts, 0);
  const inHerd = pick !== null && total > 1 && counts[pick] === top;

  const choose = (i: number) => {
    haptic.tap();
    void g.run(() => api.guess<View>("stado", String(i), date));
  };

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="СТАДО" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-3">
        <div className="mt-2 text-center text-5xl">🐑</div>
        <p className="mt-3 text-center text-lg font-bold leading-snug">{v.q}</p>

        {!v.answered ? (
          <div className="mt-5 space-y-2">
            {v.options.map((o, i) => (
              <button
                key={i}
                type="button"
                onClick={() => choose(i)}
                disabled={g.busy}
                className="w-full rounded-2xl bg-tgsection px-4 py-3.5 text-left text-[15px] leading-snug active:opacity-70 disabled:opacity-40"
              >
                {o}
              </button>
            ))}
            <div className="pt-1 text-center text-xs text-tghint">обирай не своє - обирай те, що обере більшість</div>
          </div>
        ) : (
          <div className="mt-5 space-y-2">
            {v.options.map((o, i) => {
              const pct = total > 0 ? Math.round(((counts[i] ?? 0) / total) * 100) : 0;
              const mine = pick === i;
              const leader = total > 0 && (counts[i] ?? 0) === top && top > 0;
              return (
                <div key={i} className={`relative overflow-hidden rounded-2xl px-4 py-3.5 ${mine ? "bg-tgbutton/20" : "bg-tgsection"}`}>
                  <div className="absolute inset-y-0 left-0 bg-[rgba(128,128,128,0.18)]" style={{ width: `${pct}%`, transition: "width .4s" }} />
                  <div className="relative flex items-center gap-2">
                    <span className="flex-1 text-[15px] leading-snug">
                      {leader ? "🐑 " : ""}
                      {o}
                    </span>
                    <span className="shrink-0 text-sm font-semibold text-tghint">
                      {mine ? "твій · " : ""}
                      {pct}%
                    </span>
                  </div>
                </div>
              );
            })}
            <div className="pt-2 text-center text-sm font-semibold">
              {total <= 1
                ? "ти перший - стадо ще збирається"
                : inHerd
                  ? "ти в стаді 🐑"
                  : "ти чорна вівця 🐏"}
            </div>
            <button
              type="button"
              onClick={closeApp}
              className="mt-2 w-full rounded-xl bg-tgbutton py-3 text-sm font-semibold text-tgbuttontext active:opacity-80"
            >
              назад у чат
            </button>
          </div>
        )}
      </div>

      <Toast message={g.toast} />
    </Shell>
  );
}
